import { useForm } from 'react-hook-form';
import underscore from 'underscore';
import React, { useContext, useEffect, useState } from 'react';
import { GetCustomerEditAtom, SetCustomerEditAtom } from '../store/customerAtom';
import GeneralSelect from './GeneralSelect';
import CustomerTypeCheck from './CustomerTypeCheck';
import { formatFormError } from '../helpers/formartFormError';
import LogicContext from '../Context/LogicContext';
import AddCustomerImage from './AddCustomerImage';

function AddCustomerInfoForm(props) {
  const [isCorporate, setIsCorporate] = useState(false);
  const customerToEdit = GetCustomerEditAtom();
  const setCustomerEdit = SetCustomerEditAtom();
  const logicContext = useContext(LogicContext);

  const { masterData } = props;
  const masterDataGroup = underscore?.groupBy(masterData, 'Master_Type');
  const salutationList = masterDataGroup[12];
  const companyList = masterDataGroup[15];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      Customer_Type_Id: 1, // Set individual (1) as default
      customer_first_name: '',
      Customer_Work_Mobile_No: '',
      Customer_Last_Name: '',
      customer_email_address: '',
      Customer_Mobile_No: '',
      salutation_id: '',
      company_id: 1,
      Customer_Image_URL: '',
    },
  });

  // Watch Customer_Type_Id to handle corporate/individual toggle
  const customerTypeId = watch('Customer_Type_Id');

  useEffect(() => {
    // Update isCorporate based on customer type
    setIsCorporate(customerTypeId == 3);
  }, [customerTypeId]);

  useEffect(() => {
    if (customerToEdit?.Customer_Id) {
      // Populate form with edit data
      reset({
        Customer_Type_Id: customerToEdit.Customer_Type_Id,
        customer_first_name: customerToEdit.Customer_First_Name || '',
        Customer_Work_Mobile_No: customerToEdit.Customer_Work_Mobile_No || '',
        Customer_Last_Name: customerToEdit.Customer_Last_Name || '',
        customer_email_address: customerToEdit.Customer_Email_Address || '',
        Customer_Mobile_No: customerToEdit.Customer_Mobile_No || '',
        salutation_id: customerToEdit.Salutation_Id || '',
        company_id: customerToEdit.Company_Id || 1,
        Customer_Image_URL: customerToEdit.Customer_Image_URL || '',
      });

      setIsCorporate(customerToEdit.Customer_Type_Id == 3);
    }

    return () => {
      // Cleanup
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem('profile_img');
      }
    };
  }, [customerToEdit, reset]);

  function customerTypeSelect(sel) {
    setValue('Customer_Type_Id', sel, { shouldValidate: true });
    setValue('company_id', sel, { shouldValidate: true });
  }

  function onErrors(errors) {
    console.error(errors);
    const errorMessages = formatFormError(errors);
    logicContext.showToast(errorMessages, 'error');
  }

  const onUploadedImageUrl = imageUrl => {
    setValue('Customer_Image_URL', imageUrl, { shouldValidate: false });
    setCustomerEdit({ ...customerToEdit, Customer_Image_URL: imageUrl });
  };

  const onSubmit = data => {
    console.log('SUBMIT ', data);
    props.submitGeneralInfo(data);
  };

  const salutationSelect = selection => {
    setValue('salutation_id', selection?.Code, { shouldValidate: false });
  };

  const onCompanySelect = company => {
    setValue('company_id', company?.Code, { shouldValidate: true });
  };

  return (
    <section className="w-full max-w-6xl mx-auto mt-6 p-6 bg-white rounded-lg shadow-md">
      <form onSubmit={handleSubmit(onSubmit, onErrors)} className="space-y-6">
        <div className="flex justify-between items-start">
          <CustomerTypeCheck
            {...register('Customer_Type_Id', { required: 'Customer Type is required' })}
            defaultVal={customerToEdit?.Customer_Type_Id || 1}
            customerTypeSelect={customerTypeSelect}
          />
          <section className="hidden md:block">
            <AddCustomerImage defaultVal={customerToEdit?.Customer_Image_URL} onUploadedImageUrl={onUploadedImageUrl} />
            <span className="text-center font-bold ml-2 block">Profile Image</span>
          </section>
        </div>

        <section>
          <h3 className="font-bold text-lg text-gray-900 border-b border-gray-200 pb-2">Primary Contact Details</h3>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salutation <span className="text-gray-500">(Optional)</span>
                </label>
                <GeneralSelect onOptionSelect={salutationSelect} defaultVal={customerToEdit?.Salutation_Name} list={salutationList} />
              </div>
              {isCorporate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company <span className="text-red-500">*</span>
                  </label>
                  <GeneralSelect onOptionSelect={onCompanySelect} defaultVal={customerToEdit?.Corporate_Name} list={companyList} />
                  <input type="hidden" {...register('company_id', { required: isCorporate ? 'Company is required for corporate customers' : false })} />
                  {errors?.company_id && <p className="mt-1 text-sm text-red-600">{errors.company_id.message}</p>}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Contact First Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter first name"
                  {...register('customer_first_name', {
                    required: 'First name is required',
                    minLength: {
                      value: 2,
                      message: 'First name must be at least 2 characters',
                    },
                  })}
                />
                {errors?.customer_first_name && <p className="mt-1 text-sm text-red-600">{errors.customer_first_name.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Work Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter work number"
                  {...register('Customer_Work_Mobile_No', {
                    required: 'Work phone number is required',
                    minLength: {
                      message: 'Phone number should be at least 6 characters',
                    },
                    // pattern: {
                    //   value: /^[0-9+\-\s()]+$/,
                    //   message: 'Please enter a valid phone number',
                    // },
                  })}
                />
                {errors?.Customer_Work_Mobile_No && <p className="mt-1 text-sm text-red-600">{errors.Customer_Work_Mobile_No.message}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Contact Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter last name"
                  {...register('Customer_Last_Name', {
                    required: 'Last name is required',
                    minLength: {
                      value: 2,
                      message: 'Last name must be at least 2 characters',
                    },
                  })}
                />
                {errors?.Customer_Last_Name && <p className="mt-1 text-sm text-red-600">{errors.Customer_Last_Name.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter mobile number"
                  {...register('Customer_Mobile_No', {
                    required: 'Mobile number is required',
                    minLength: {
                      message: 'Mobile number should be at least 6 characters',
                    },
                    // pattern: {
                    //   value: /^[0-9+\-\s()]+$/,
                    //   message: 'Please enter a valid mobile number',
                    // },
                  })}
                />
                {errors?.Customer_Mobile_No && <p className="mt-1 text-sm text-red-600">{errors.Customer_Mobile_No.message}</p>}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter email"
                {...register('customer_email_address', {
                  required: 'Email address is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address',
                  },
                })}
              />
              {errors?.customer_email_address && <p className="mt-1 text-sm text-red-600">{errors.customer_email_address.message}</p>}
            </div>
          </div>
        </section>

        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-6 py-2 rounded-md transition-colors duration-200"
          >
            Save
          </button>
        </div>
      </form>
    </section>
  );
}

export default AddCustomerInfoForm;